import { useState, useCallback } from 'react';
import { message } from 'antd';
import { finBillDataApi, FINBillData } from '../../../services/finBillDataApi';
import { useAuth } from '../../../contexts/AuthContext';
import { useFieldErrors } from '../../../hooks/useFieldErrors';
import { Mode } from './useFinBillDataMode';

export function useFinBillDataRecord() {
  const { user } = useAuth();
  const [record, setRecord] = useState<FINBillData | null>(null);
  const [formData, setFormData] = useState<Partial<FINBillData>>({});
  const [loading, setLoading] = useState(false);
  const { setFieldErrors, clearFieldErrors, getFieldError, hasErrors } = useFieldErrors();

  /** Load a single record by ID */
  const loadRecord = useCallback(async (id: number): Promise<FINBillData | null> => {
    setLoading(true);
    try {
      const data = await finBillDataApi.get(id);
      setRecord(data);
      setFormData({ ...data });
      return data;
    } catch {
      message.error('載入失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /** Set a single field */
  const setField = useCallback((key: keyof FINBillData, value: FINBillData[keyof FINBillData]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a single field value */
  const getField = useCallback((key: keyof FINBillData): FINBillData[keyof FINBillData] => {
    return (formData as Record<string, FINBillData[keyof FINBillData]>)[key] ?? '';
  }, [formData]);

  /** Save (create or update) */
  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: FINBillData) => void,
  ) => {
    setLoading(true);
    clearFieldErrors();
    try {
      if (mode === 'add') {
        const result = await finBillDataApi.create(formData);
        message.success(`新增成功: #${result.bill_mid}`);
        onSuccess(result);
      } else {
        const result = await finBillDataApi.update(formData.bill_mid!, formData);
        message.success('修改成功');
        setRecord(result);
        setFormData({ ...result });
        onSuccess(result);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: { field: string; message: string }[]; error?: string } } };
      const errors = err.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        setFieldErrors(errors);
        message.error('請修正標示的欄位');
      } else {
        message.error(err.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, clearFieldErrors, setFieldErrors]);

  /** Delete the current record */
  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!record) return false;
    try {
      await finBillDataApi.delete(record.bill_mid);
      message.success('刪除成功');
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    }
  }, [record]);

  /** Reset form to current record (revert) */
  const revertToRecord = useCallback(() => {
    clearFieldErrors();
    if (record) {
      setFormData({ ...record });
    }
  }, [record, clearFieldErrors]);

  /** Reset form for a new add */
  const resetForAdd = useCallback(() => {
    clearFieldErrors();
    setRecord(null);
    setFormData({
      bill_type: 'AP',
      bill_sort: 'P',
      status: 'O',
      curr_code: 'NTD',
      rate: 1,
      check_amt: 0,
      c_user: user?.userId || '',
    });
  }, [user, clearFieldErrors]);

  /** Apply result after save */
  const applyResult = useCallback((result: FINBillData) => {
    setRecord(result);
    setFormData({ ...result });
  }, []);

  return {
    record, formData, loading,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    revertToRecord, resetForAdd, applyResult,
    getFieldError, hasErrors,
  };
}
